// ActionQueue.js — the patient-lifecycle worklist: nudges with one-click
// fix, long-run agent tasks (pending → working → resolved), and the
// Review & Sign entry card. Caps visible cards with a "+N more" expander.
//
// Action feedback phases: click → pulsing green ring surrounds the card →
// spinner ("applying to note…") fades in a beat later → card exits IN
// PLACE (useAnimatedList holds departed cards at their position through
// the slide-and-collapse; neighbors only move as the height collapses).
import React, { useEffect, useRef, useState } from 'react';
import { useComprehendLive } from 'agent/useComprehendLive';
import { QUEUE_KINDS } from 'agent/liveStore';
import {
  T,
  card,
  severityColor,
  buttonPrimary,
  buttonGhost,
} from './liveTheme';

const VISIBLE_CAP = 4; // tasks
const GROUP_CAP = 3; // patient groups shown before "+N more"
const LEAVE_MS = 650; // matches cplCardOut

/**
 * Stable-position animated list: departed items keep their slot (flagged
 * `leaving`) until the exit animation finishes, instead of being torn out
 * and re-appended elsewhere.
 */
const useAnimatedList = (items) => {
  const [, force] = useState(0);
  const orderRef = useRef([]); // ids in stable render order
  const mapRef = useRef(new Map()); // id -> {item, leaving}
  // Hash includes status: a pending->working flip must refresh the stored
  // item (id-only hashing left cards rendering stale pending snapshots,
  // which is why the working ring/spinner never appeared).
  const ids = items.map((i) => `${i.id}:${i.status}`).join(',');

  useEffect(() => {
    const live = new Map(items.map((i) => [i.id, i]));
    // Refresh live entries; flag departures in place.
    for (const id of [...mapRef.current.keys()]) {
      const entry = mapRef.current.get(id);
      if (live.has(id)) {
        entry.item = live.get(id);
        entry.leaving = false;
      } else if (!entry.leaving) {
        entry.leaving = true;
        setTimeout(() => {
          mapRef.current.delete(id);
          orderRef.current = orderRef.current.filter((x) => x !== id);
          force((n) => n + 1);
        }, LEAVE_MS);
      }
    }
    // NEWEST FIRST: newcomers go to the TOP (the chat layer covers the
    // bottom of the queue, and the freshest nudge is usually about the
    // patient on the table right now). A group jumps to the top when it
    // receives a fresh nudge; its older items follow within the group.
    for (const item of items) {
      if (!mapRef.current.has(item.id)) {
        mapRef.current.set(item.id, { item, leaving: false });
        orderRef.current.unshift(item.id);
      }
    }
    force((n) => n + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  return orderRef.current.map((id) => mapRef.current.get(id)).filter(Boolean);
};

const SectionLabel = ({ children }) => (
  <p
    style={{
      margin: '12px 0 6px',
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '.7px',
      color: T.textFaint,
    }}
  >
    {children}
  </p>
);

export const NudgeCard = ({ item, actions, hidePatient }) => {
  const working = item.status === 'working';
  // Orbit phases: the green light circles the CARD first, then moves to
  // the action pill until the accept lands and the card slides away.
  const [orbitPhase, setOrbitPhase] = useState('card');
  useEffect(() => {
    if (!working) {
      setOrbitPhase('card');
      return undefined;
    }
    setOrbitPhase('card');
    const t = setTimeout(() => setOrbitPhase('button'), 700);
    return () => clearTimeout(t);
  }, [working]);
  return (
    <div
      className={working && orbitPhase === 'card' ? 'cpl-orbit' : undefined}
      onClick={() => actions.openDrillIn(item.conversationId)}
      role="button"
      title="Tap for patient details"
      style={{
        ...card(severityColor(item.severity)),
        padding: '6px 8px',
        marginBottom: 6,
        cursor: 'pointer',
        animation: 'cplSlide .2s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 11,
              fontWeight: 600,
              color: T.textPrimary,
              lineHeight: 1.35,
            }}
          >
            {item.title}
            {!hidePatient && item.patientName ? ` — ${item.patientName}` : ''}
          </p>
          {item.proposedText && (
            <p
              style={{
                margin: '3px 0 0',
                fontSize: 10,
                color: T.textSecondary,
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              “{item.proposedText}”
            </p>
          )}
        </div>
        {working ? (
          <span
            className={orbitPhase === 'button' ? 'cpl-orbit-sm' : undefined}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: 'rgba(107,216,136,0.12)',
              color: T.greenBright,
              fontSize: 10,
              fontWeight: 600,
              padding: '3px 9px',
              borderRadius: 6,
              flexShrink: 0,
            }}
          >
            applying…
          </span>
        ) : (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ display: 'flex', gap: 5, flexShrink: 0 }}
          >
            <button
              onClick={() => actions.fixItem(item)}
              title="Fix note"
              aria-label="Fix note"
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                border: 'none',
                background: T.green,
                color: T.greenDark,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i
                className="fa fa-check"
                aria-hidden="true"
                style={{ fontSize: 11 }}
              />
            </button>
            <button
              onClick={() => actions.dismissItem(item)}
              title="Dismiss"
              aria-label="Dismiss"
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                border: `1px solid ${T.border}`,
                background: 'transparent',
                color: T.textFaint,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i
                className="fa fa-times"
                aria-hidden="true"
                style={{ fontSize: 11 }}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const TaskCard = ({ item, actions }) => (
  <div
    className={item.status === 'working' ? 'cpl-orbit' : undefined}
    style={card(T.teal)}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <i
        className={
          item.status === 'working'
            ? 'fa fa-circle-notch fa-spin'
            : item.status === 'failed'
              ? 'fa fa-exclamation-triangle'
              : 'fa fa-cloud-upload-alt'
        }
        aria-hidden="true"
        style={{
          color: item.status === 'failed' ? T.redSoft : T.tealSoft,
          fontSize: 13,
        }}
      />
      <p style={{ margin: 0, fontSize: 12, color: T.textPrimary, flex: 1 }}>
        {item.title}
      </p>
      {item.status === 'working' && (
        <span style={{ color: T.tealSoft, fontSize: 10, fontWeight: 600 }}>
          working
        </span>
      )}
      {item.conversationId && item.status !== 'working' && (
        <button
          style={buttonGhost}
          onClick={() => actions.openVisit(item.conversationId)}
        >
          Open
        </button>
      )}
    </div>
    {item.detail && (
      <p style={{ margin: '4px 0 0', fontSize: 10, color: T.textSecondary }}>
        {item.detail}
      </p>
    )}
    {item.status === 'working' && (
      <div
        style={{
          marginTop: 6,
          height: 3,
          background: 'rgba(255,255,255,0.08)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: '40%',
            height: '100%',
            background: T.teal,
            borderRadius: 2,
            animation: 'cplWork 1.8s ease-in-out infinite',
          }}
        />
      </div>
    )}
  </div>
);

const ResolvedRow = ({ item, actions }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '4px 10px',
      marginBottom: 6,
      color: T.greenBright,
      fontSize: 11,
      animation: 'cplSlide .3s ease',
    }}
  >
    <i className="fa fa-check" aria-hidden="true" style={{ fontSize: 11 }} />
    <span style={{ flex: 1 }}>
      {item.detail || 'Applied'} — {item.patientName || item.title}
    </span>
    {item.undo && (
      <span
        onClick={() => actions.undoItem(item)}
        style={{
          color: T.textSecondary,
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        Undo
      </span>
    )}
  </div>
);

const ActionQueue = () => {
  const { state, actions } = useComprehendLive();
  const [showAll, setShowAll] = useState(false);

  const pendingNudges = state.queue.filter(
    (q) =>
      q.kind === QUEUE_KINDS.NUDGE &&
      (q.status === 'pending' || q.status === 'working'),
  );
  const tasksAndActions = state.queue.filter(
    (q) =>
      q.kind === QUEUE_KINDS.ACTION &&
      ['pending', 'working', 'failed'].includes(q.status),
  );
  const recentlyResolved = state.queue
    .filter((q) => q.status === 'resolved')
    .slice(-2);
  const reviews = state.queue.filter(
    (q) => q.kind === QUEUE_KINDS.REVIEW && q.status === 'pending',
  );

  const animatedNudges = useAnimatedList(pendingNudges);
  const animatedTasks = useAnimatedList(tasksAndActions);
  const [openGroups, setOpenGroups] = useState({});
  const [acceptingGroup, setAcceptingGroup] = useState(null);

  // Group nudges by patient (stable first-seen order).
  const nudgeGroups = [];
  {
    const byPatient = new Map();
    for (const entry of animatedNudges) {
      const key = entry.item.patientName || 'General';
      if (!byPatient.has(key)) {
        const group = { patient: key, entries: [] };
        byPatient.set(key, group);
        nudgeGroups.push(group);
      }
      byPatient.get(key).entries.push(entry);
    }
  }
  const visibleGroups = showAll ? nudgeGroups : nudgeGroups.slice(0, GROUP_CAP);

  const isGroupOpen = (patient) => openGroups[patient] ?? true;

  const acceptAll = async (group) => {
    setAcceptingGroup(group.patient);
    try {
      const items = group.entries
        .filter((e) => !e.leaving && e.item.status === 'pending')
        .map((e) => e.item);
      for (const item of items) {
        // Sequential so each card plays its choreography (and note edits
        // don't race each other).
        // eslint-disable-next-line no-await-in-loop
        await actions.fixItem(item);
      }
    } finally {
      setAcceptingGroup(null);
    }
  };

  // When an action targets a card that's scrolled away (or hidden behind
  // the +N more cap), reveal it and bring it to center before the
  // choreography plays.
  useEffect(() => {
    const focusId = state.queueFocusItemId;
    if (!focusId) return undefined;
    const focused = pendingNudges.find((q) => q.id === focusId);
    if (focused) {
      const key = focused.patientName || 'General';
      setOpenGroups((g) => ({ ...g, [key]: true }));
      const groupIdx = nudgeGroups.findIndex((gr) => gr.patient === key);
      if (groupIdx >= GROUP_CAP && !showAll) setShowAll(true);
    }
    const t = setTimeout(() => {
      document
        .getElementById(`cpl-q-${focusId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.queueFocusAt]);

  return (
    <div>
      {nudgeGroups.length > 0 && (
        <>
          <SectionLabel>CLINICAL NUDGES</SectionLabel>
          {visibleGroups.map((group) => {
            const open = isGroupOpen(group.patient);
            const liveCount = group.entries.filter(
              (e) => !e.leaving && e.item.status !== 'dismissed',
            ).length;
            return (
              <div key={group.patient} style={{ marginBottom: 6 }}>
                <div
                  onClick={() =>
                    setOpenGroups((g) => ({ ...g, [group.patient]: !open }))
                  }
                  role="button"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    padding: '5px 8px',
                    background: T.surfaceMuted,
                    border: `1px solid ${T.border}`,
                    borderRadius: open ? '8px 8px 0 0' : 8,
                    cursor: 'pointer',
                  }}
                >
                  <i
                    className={`fa fa-chevron-${open ? 'down' : 'right'}`}
                    aria-hidden="true"
                    style={{ color: T.textFaint, fontSize: 9, width: 10 }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: T.textPrimary,
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {group.patient}
                  </span>
                  <span
                    style={{
                      background: 'rgba(251,191,36,0.16)',
                      color: T.amber,
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '0 7px',
                      borderRadius: 8,
                    }}
                  >
                    {liveCount}
                  </span>
                  {liveCount > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        acceptAll(group);
                      }}
                      disabled={acceptingGroup === group.patient}
                      style={{
                        ...buttonPrimary,
                        padding: '3px 10px',
                        fontSize: 11,
                        opacity: acceptingGroup === group.patient ? 0.6 : 1,
                      }}
                    >
                      {acceptingGroup === group.patient
                        ? 'Accepting…'
                        : 'Accept all'}
                    </button>
                  )}
                </div>
                {open && (
                  <div
                    style={{
                      border: `1px solid ${T.border}`,
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      padding: '6px 6px 0',
                    }}
                  >
                    {group.entries.map(({ item, leaving }) => (
                      <div
                        key={item.id}
                        id={`cpl-q-${item.id}`}
                        className={leaving ? 'cpl-card-out' : undefined}
                      >
                        <NudgeCard item={item} actions={actions} hidePatient />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {nudgeGroups.length > GROUP_CAP && (
            <p
              onClick={() => setShowAll(!showAll)}
              style={{
                margin: '0 0 4px',
                fontSize: 11,
                color: T.textSecondary,
                cursor: 'pointer',
                textAlign: 'center',
              }}
            >
              {showAll
                ? 'Show less'
                : `+${nudgeGroups.length - GROUP_CAP} more patients`}
            </p>
          )}
        </>
      )}

      {animatedTasks.length > 0 && (
        <>
          <SectionLabel>ACTIONS</SectionLabel>
          {animatedTasks.map(({ item, leaving }) => (
            <div
              key={item.id}
              id={`cpl-q-${item.id}`}
              className={leaving ? 'cpl-card-out' : undefined}
            >
              <TaskCard item={item} actions={actions} />
            </div>
          ))}
        </>
      )}

      {recentlyResolved.map((item) => (
        <ResolvedRow key={item.id} item={item} actions={actions} />
      ))}

      {reviews.length > 0 && (
        <>
          <SectionLabel>REVIEW &amp; SIGN</SectionLabel>
          <div style={card(T.greenBright)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <i
                className="fa fa-copy"
                aria-hidden="true"
                style={{ color: T.greenBright, fontSize: 15 }}
              />
              <div style={{ flex: 1 }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    fontWeight: 600,
                    color: T.textPrimary,
                  }}
                >
                  {reviews.length} note{reviews.length > 1 ? 's' : ''} ready to
                  review
                </p>
                <p
                  style={{
                    margin: '1px 0 0',
                    fontSize: 10,
                    color: T.textSecondary,
                  }}
                >
                  swipe to accept · hold mic to correct · sign in your EMR
                </p>
              </div>
              <button
                style={buttonPrimary}
                onClick={() => actions.setReviewMode(true)}
              >
                Start
              </button>
            </div>
          </div>
        </>
      )}

      {!pendingNudges.length && !tasksAndActions.length && !reviews.length && (
        <p
          style={{
            margin: '14px 0',
            fontSize: 11,
            color: T.textFaint,
            textAlign: 'center',
          }}
        >
          {state.loading ? 'Checking your day…' : 'All caught up.'}{' '}
          {!state.loading && (
            <span
              onClick={actions.refresh}
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              Refresh
            </span>
          )}
        </p>
      )}
    </div>
  );
};

export default ActionQueue;
